<template>
  <section ref="sectionRef" class="metricas">
    <div class="metricas__transition-in"></div>
    <div ref="atmosphereRef" class="metricas__atmosphere"></div>
    <div class="metricas__volumetric"></div>
    <ParticleField
      :count="40"
      :speed="0.15"
      :opacity="0.25"
      color="52, 211, 153"
      :min-size="0.3"
      :max-size="1.5"
    />

    <div class="metricas__inner">
      <div ref="gridRef" class="metricas__grid">
        <div
          v-for="(metric, index) in metrics"
          :key="index"
          ref="metricRefs"
          class="metric"
          :style="{ '--accent': metric.color }"
        >
          <span class="metric__prefix">{{ metric.prefix }}</span>
          <span class="metric__value" :data-target="metric.value">0</span>
          <span class="metric__suffix">{{ metric.suffix }}</span>
          <p class="metric__label">{{ metric.label }}</p>
        </div>
      </div>
    </div>

    <div class="metricas__transition-out"></div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ParticleField from "./ParticleField.vue";

gsap.registerPlugin(ScrollTrigger);

const sectionRef = ref(null);
const atmosphereRef = ref(null);
const gridRef = ref(null);
const metricRefs = ref([]);

const metrics = [
  {
    prefix: "+",
    value: 300,
    suffix: "%",
    label: "Aumento de Visibilidade Online",
    color: "var(--color-metric-green)",
  },
  {
    prefix: "-",
    value: 50,
    suffix: "%",
    label: "Otimização de Processos Manuais",
    color: "var(--color-metric-blue)",
  },
  {
    prefix: "+",
    value: 80,
    suffix: "%",
    label: "Melhora na Experiência do Cliente",
    color: "var(--color-metric-purple)",
  },
  {
    prefix: "+",
    value: 40,
    suffix: "%",
    label: "Aumento de Receita",
    color: "var(--color-metric-amber)",
  },
];

let ctx;

onMounted(() => {
  ctx = gsap.context(() => {
    gsap.to(atmosphereRef.value, {
      y: 60,
      ease: "none",
      scrollTrigger: {
        trigger: sectionRef.value,
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
      },
    });

    gsap.to(gridRef.value, {
      y: -30,
      ease: "none",
      scrollTrigger: {
        trigger: sectionRef.value,
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
      },
    });

    metricRefs.value.forEach((el, i) => {
      const valueEl = el.querySelector(".metric__value");
      const target = parseInt(valueEl.dataset.target);
      const counter = { val: 0 };

      gsap.fromTo(
        el,
        { autoAlpha: 0, y: 50, scale: 0.9 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          ease: "power3.out",
          delay: i * 0.1,
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.to(counter, {
        val: target,
        duration: 2,
        ease: "power2.out",
        snap: { val: 1 },
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
        onUpdate() {
          valueEl.textContent = Math.round(counter.val);
        },
      });
    });
  }, sectionRef.value);
});

onUnmounted(() => {
  if (ctx) ctx.revert();
});
</script>

<style scoped>
.metricas {
  position: relative;
  min-height: 100vh;
  padding: 8rem 2rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.metricas__transition-in {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(to bottom, var(--color-bg-deep), transparent);
  z-index: 4;
  pointer-events: none;
}

.metricas__transition-out {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(to bottom, transparent, var(--color-bg-deep));
  z-index: 4;
  pointer-events: none;
}

.metricas__atmosphere {
  position: absolute;
  inset: -20%;
  background: radial-gradient(
      ellipse at 40% 30%,
      rgba(52, 211, 153, 0.06) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 70% 70%,
      rgba(96, 165, 250, 0.05) 0%,
      transparent 50%
    );
  z-index: 0;
  animation: metricasDrift 18s ease-in-out infinite alternate;
  will-change: transform;
}

.metricas__volumetric {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 600px;
  height: 400px;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    ellipse,
    rgba(52, 211, 153, 0.05) 0%,
    rgba(96, 165, 250, 0.03) 40%,
    transparent 70%
  );
  filter: blur(100px);
  mix-blend-mode: screen;
  z-index: 1;
  pointer-events: none;
}

@keyframes metricasDrift {
  0% {
    transform: translate(0, 0) scale(1);
  }
  100% {
    transform: translate(15px, -10px) scale(1.03);
  }
}

.metricas__inner {
  position: relative;
  z-index: 5;
  max-width: 1100px;
  width: 100%;
}

.metricas__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4rem 3rem;
  will-change: transform;
}

.metric {
  text-align: center;
  visibility: hidden;
  position: relative;
}

.metric::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: var(--accent);
  opacity: 0.08;
  filter: blur(60px);
  mix-blend-mode: screen;
  pointer-events: none;
}

.metric::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: var(--accent);
  opacity: 0.04;
  filter: blur(30px);
  mix-blend-mode: color-dodge;
  pointer-events: none;
}

.metric__prefix,
.metric__suffix {
  font-family: var(--font-display);
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  color: var(--accent);
}

.metric__value {
  font-family: var(--font-display);
  font-size: clamp(3rem, 7vw, 5rem);
  font-weight: 700;
  color: var(--color-text-primary);
  letter-spacing: -0.02em;
}

.metric__label {
  margin-top: 0.75rem;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
  max-width: 220px;
  margin-left: auto;
  margin-right: auto;
}

@media (max-width: 640px) {
  .metricas__grid {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  .metricas {
    padding: 5rem 1.5rem;
  }
}
</style>
